import { geoCentroid } from 'd3-geo'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from 'react-simple-maps'
import { selectCompanyInfo } from '../../../../alerts/alertDetail/store/slice'
import { UseTypeFontSize } from '../../../../common/hooks/useTypeFontSize'
import { Element } from '../../../../common/html/element'
import { Loading } from '../../../../common/loading'
import EventEmitter, {
  DID_MOUNT_COMPONENT,
} from '../../../../helpers/EventEmitter'
import { getFontSize } from '../../../../helpers/fontSize'
import { formatVal, valToPercent } from '../../../../helpers/value'
import { COMPANY_NON_BANK_TYPES, COMPANY_TYPES } from '../../../../utils/common'
import { DefaultVsicName } from '../DefaultVsicName'
import classes from './index.module.scss'
import { selectData } from './store/slice'

const colorRanges = [
  { min: 1, max: 100, color: '#B6D9FF', label: '1 - 100' },
  { min: 101, max: 500, color: '#71B6FF', label: '101 - 500' },
  { min: 501, max: 1000, color: '#3381D3', label: '501 - 1,000' },
  { min: 1001, max: 10000, color: '#0755A7', label: '1,001 - 10,000' },
  { min: 10001, max: Infinity, color: '#003873', label: '10,001+' },
]

export const BusinessProvinceMap = () => {
  const locale = useSelector((state) => state.i18n.locale)
  const typeFontSize = UseTypeFontSize()
  const fontSizeTitle = getFontSize(8, typeFontSize)
  const companyInfo = useSelector(selectCompanyInfo)
  const data = useSelector(selectData)
  const [geoData, setGeoData] = useState(null)
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    data: {},
  })

  const top3 = [...data]
    .sort((a, b) => b.numberBusiness - a.numberBusiness)
    .slice(0, 3)

  const getProvinceData = (locationCode) => {
    if (!locationCode) return null
    return (
      data.find(
        (item) => item.locationCode === locationCode.trim().toLowerCase(),
      ) || null
    )
  }

  const handleMouseMove = (evt, geo) => {
    const locationCode = geo.properties.ma_tinh
    const data = getProvinceData(locationCode)
    if (!data) return
    setTooltip({
      visible: true,
      x: evt.clientX + 15,
      y: evt.clientY + 15,
      data: data,
    })
  }

  const handleMouseLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0, content: '' })
  }

  const getProvinceColor = (locationCode) => {
    const info = getProvinceData(locationCode)
    if (!info) return '#E0E0E0'
    const numberBusiness = info.numberBusiness
    const colorRange = colorRanges.find(
      (range) => numberBusiness >= range.min && numberBusiness <= range.max,
    )
    return colorRange ? colorRange.color : '#E0E0E0'
  }

  const getCompanyTypeName = () => {
    switch (companyInfo.companyType) {
      case COMPANY_TYPES.BANK:
        return <Translate value="industryOverview.BANKING_INDUSTRY" />
      case COMPANY_TYPES.NON_BANK:
        switch (companyInfo.companyTypeOrigin) {
          case COMPANY_NON_BANK_TYPES.NON_BANK_FIC:
            return <Translate value="industryOverview.NON_BANK_FIC_INDUSTRY" />
          case COMPANY_NON_BANK_TYPES.NON_BANK_FIO:
            return <Translate value="industryOverview.NON_BANK_FIO_INDUSTRY" />
          case COMPANY_NON_BANK_TYPES.NON_BANK_LEA:
            return <Translate value="industryOverview.NON_BANK_LEA_INDUSTRY" />
          default:
            return ''
        }
      case COMPANY_TYPES.SECURITIES:
        return <Translate value="industryOverview.SECURITIES_INDUSTRY" />
      case COMPANY_TYPES.LIFE_INSURANCE:
        return <Translate value="industryOverview.LIFE_INSURANCE_INDUSTRY" />
      case COMPANY_TYPES.NON_LIFE_INSURANCE:
        return (
          <Translate value="industryOverview.NON_LIFE_INSURANCE_INDUSTRY" />
        )
      default:
        return <DefaultVsicName isLevel1={false} isOnSticky />
    }
  }

  useEffect(() => {
    fetch('/data/vietnam2025.json')
      .then((res) => res.json())
      .then((data) => setGeoData(data))
  }, [locale])

  useEffect(() => {
    if (geoData) {
      requestAnimationFrame(() => {
        EventEmitter.dispatch(DID_MOUNT_COMPONENT)
      })
    }
  }, [geoData])

  if (!geoData)
    return (
      <div style={{ height: 500, position: 'relative' }}>
        <Loading />
      </div>
    )

  return (
    <Element id="businessProvinceMapId">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 1950,
          center: [107.8, 15.6], // Tọa độ trung tâm (giữa Việt Nam)
        }}
        style={{ width: '100%', height: 700 }}
      >
        <Geographies geography={geoData}>
          {({ geographies }) => (
            <>
              {geographies.map((geo) => {
                const locationCode = geo.properties.ma_tinh
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseMove={(evt) => handleMouseMove(evt, geo)}
                    onMouseLeave={handleMouseLeave}
                    style={{
                      default: {
                        fill: getProvinceColor(locationCode),
                        stroke: '#333',
                        strokeWidth: 0.5,
                        outline: 'none',
                      },
                      hover: {
                        fill: getProvinceColor(locationCode),
                        stroke: '#FA825A',
                        outline: 'none',
                      },
                      pressed: {
                        fill: getProvinceColor(locationCode),
                        stroke: '#FA825A',
                        outline: 'none',
                      },
                    }}
                  />
                )
              })}
              {top3.map((item, idx) => {
                const geo = geographies.find(
                  (g) => g.properties.ma_tinh === item.locationCode,
                )
                if (!geo) return null
                const coords = geoCentroid(geo)
                return (
                  <Marker key={item.locationCode} coordinates={coords}>
                    <circle
                      r={3.5}
                      fill="none"
                      stroke="#FA825A"
                      strokeWidth={1}
                    />
                    <circle r={2} fill="#FA825A" />
                    <text
                      textAnchor="right"
                      x={6}
                      y={4}
                      style={{
                        fill: '#FA825A',
                        fontSize: fontSizeTitle,
                        fontWeight: '600',
                      }}
                    >
                      {item.provinceName}
                    </text>
                  </Marker>
                )
              })}
            </>
          )}
        </Geographies>
      </ComposableMap>
      {tooltip.visible && (
        <div
          className={classes.tooltip}
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ paddingBottom: '12px' }}>
            {tooltip.data.provinceName}
          </div>
          <div style={{ marginBottom: '6px' }}>{getCompanyTypeName()}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div className={classes.tooltipRow}>
              <span>
                <Translate value="industryOverview.businessProvince.NUMBER_OF_COMPANIES" />
              </span>
              <span>
                {formatVal(tooltip.data.numberBusiness, undefined, false, 0)}
              </span>
            </div>
            <div className={classes.tooltipRow}>
              <span>
                <Translate value="industryOverview.businessProvince.TOTAL_COMPANIES_PERCENT" />
              </span>
              <span>{valToPercent(tooltip.data.totalPercentCompanies)}</span>
            </div>
            <div className={classes.tooltipRow}>
              <span>
                <Translate value="industryOverview.businessProvince.RANK" />
              </span>
              <span>{tooltip.data.rank}</span>
            </div>
          </div>
        </div>
      )}
      <div style={{ marginBottom: 12 }}>
        <div
          className="dFlex justifyContentCenter fw600"
          style={{ marginBottom: 4 }}
        >
          <Translate value="industryOverview.businessProvince.NUMBER_OF_COMPANIES" />
        </div>
        <div
          className="dFlex justifyContentCenter alignItemsCenter"
          style={{ gap: 2 }}
        >
          {colorRanges.map((range) => (
            <div
              key={range.label}
              className="dFlex flexColumn alignItemsCenter gap1"
            >
              <div
                style={{ width: 150, height: 24, background: range.color }}
              />
              <div style={{ color: '#0755A7', fontWeight: 600 }}>
                {range.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Element>
  )
}
